/**
 * ScenarioQuest - Generates and presents questions using the Scenario + Lens system
 * 
 * One story, many mathematical lenses - same context, deeper questions
 */

import { useState, useEffect, useCallback } from 'react';
import {
  SCENARIOS,
  LENS_CONFIGS,
  LEVEL_INFO,
  generateQuestion,
  getAvailableLenses,
  type MathLevel,
  type QuestionLens,
  type GeneratedQuestion,
} from '../lib/scenarios';
import { useVoice } from '../hooks/useVoice';
import { tracker } from '../lib/tracking';

interface ScenarioQuestProps {
  scenarioId: string;
  lens: QuestionLens;
  level: MathLevel;
  childId: string;
  onComplete: (result: { score: number; passed: boolean; xpEarned: number }) => void;
  onRequestHint?: () => void;
}

export function ScenarioQuest({
  scenarioId,
  lens,
  level,
  childId,
  onComplete,
  onRequestHint,
}: ScenarioQuestProps) {
  const [question, setQuestion] = useState<GeneratedQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  const [showSteps, setShowSteps] = useState(false);
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [startTime] = useState(Date.now());
  
  const voice = useVoice({ persona: 'coach' });
  const scenario = SCENARIOS[scenarioId];
  const lensConfig = LENS_CONFIGS[lens];
  
  // Generate question on mount
  useEffect(() => {
    const seed = Date.now();
    const q = generateQuestion({ scenarioId, lens, level, seed });
    setQuestion(q);
    
    if (q) {
      tracker.trackChallenge({
        challengeId: q.id,
        templateId: scenarioId,
        skillTag: `${scenarioId}.${lens}`,
        action: 'start',
        userId: childId,
        isChild: true,
      });
      
      // Announce the quest
      setTimeout(() => {
        voice.speakLine('questStart');
      }, 500);
    }
  }, [scenarioId, lens, level]);
  
  const handleSubmit = useCallback(() => {
    if (!question || !answer.trim()) return;
    
    const isCorrect = question.acceptableAnswers.some(
      acceptable => answer.trim().toLowerCase() === acceptable.toLowerCase()
    );
    
    setAttempts(prev => prev + 1);
    
    if (isCorrect) {
      setResult('correct');
      voice.speakLine('correct');
      
      const duration = Math.round((Date.now() - startTime) / 1000);
      const score = Math.max(0.5, 1 - (attempts * 0.15)); // Reduce score per attempt
      const xpEarned = Math.round(question.xpReward * score);
      
      tracker.trackChallenge({
        challengeId: question.id,
        templateId: scenarioId,
        skillTag: `${scenarioId}.${lens}`,
        action: 'complete',
        score,
        passed: true,
        durationSec: duration,
        userId: childId,
        isChild: true,
      });
      
      setTimeout(() => {
        onComplete({ score, passed: true, xpEarned });
      }, 2000);
    } else {
      setResult('incorrect');
      voice.speakLine('incorrect');
      
      // Reset after animation
      setTimeout(() => {
        setResult(null);
        setAnswer('');
      }, 1500);
    }
  }, [question, answer, attempts, startTime, childId, scenarioId, lens]);
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  
  if (!question || !scenario || !lensConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading quest...</div>
      </div>
    );
  }
  
  return (
    <div className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
      result === 'correct' 
        ? 'ring-4 ring-blox-xp shadow-lg shadow-blox-xp/30' 
        : result === 'incorrect'
        ? 'ring-4 ring-blox-error animate-shake'
        : ''
    }`}>
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{ 
          background: `linear-gradient(135deg, ${scenario.theme.color}22, transparent)` 
        }}
      />
      
      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{scenario.theme.emoji}</span>
            <div>
              <h2 className="font-display text-lg text-white">{scenario.name}</h2>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded bg-blox-surface text-slate-300">
                  {lensConfig.name}
                </span>
                <span className="px-2 py-0.5 rounded bg-blox-accent/20 text-blox-accent">
                  {LEVEL_INFO[level].name}
                </span>
              </div>
            </div>
          </div>
          
          {/* XP reward */}
          <div className="text-right">
            <p className="text-2xl font-display text-blox-xp">+{question.xpReward}</p>
            <p className="text-xs text-slate-400">XP</p>
          </div>
        </div>
        
        {/* Context */}
        <div className="mb-4 p-4 rounded-xl bg-blox-dark/50">
          <p className="text-sm text-slate-300 whitespace-pre-line">{question.context}</p>
        </div>
        
        {/* The Question */}
        <div className="mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Quest Objective</p>
          <p className="text-lg text-white font-medium">{question.question}</p>
        </div>
        
        {/* Answer input */}
        <div className="mb-4">
          <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
            Your Answer
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your answer..."
              disabled={result === 'correct'}
              className={`flex-1 px-4 py-3 rounded-xl border bg-blox-dark text-white placeholder-slate-500
                focus:outline-none focus:ring-2 transition-all font-mono text-lg
                ${result === 'correct' 
                  ? 'border-blox-xp focus:ring-blox-xp/30' 
                  : result === 'incorrect'
                  ? 'border-blox-error focus:ring-blox-error/30'
                  : 'border-blox-surface focus:border-blox-accent focus:ring-blox-accent/30'
                }`}
            />
            <button
              onClick={handleSubmit}
              disabled={!answer.trim() || result === 'correct'}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                result === 'correct'
                  ? 'bg-blox-xp text-blox-dark'
                  : 'bg-blox-accent text-blox-dark hover:bg-blox-accent/90 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {result === 'correct' ? '✓ Correct!' : 'Submit'}
            </button>
          </div>
          
          {/* Attempt counter */}
          {attempts > 0 && result !== 'correct' && (
            <p className="text-xs text-slate-500 mt-2">
              Attempts: {attempts} {attempts > 1 && '(XP reduced)'}
            </p>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-3">
          {/* Show steps toggle */}
          {question.workingSteps && question.workingSteps.length > 0 && (
            <button
              onClick={() => setShowSteps(!showSteps)}
              className="px-4 py-2 rounded-lg text-sm bg-blox-surface text-slate-300 hover:bg-blox-card transition-colors"
            >
              {showSteps ? '🙈 Hide Steps' : '👀 Show Working'}
            </button>
          )}
          
          {/* Hint button */}
          {onRequestHint && result !== 'correct' && (
            <button
              onClick={onRequestHint}
              className="px-4 py-2 rounded-lg text-sm bg-blox-rare/20 text-blox-rare border border-blox-rare/30 hover:bg-blox-rare/30 transition-colors"
            >
              💡 Need a hint?
            </button>
          )}
        </div>
        
        {/* Working steps (when revealed) */}
        {showSteps && question.workingSteps && (
          <div className="mt-4 p-4 rounded-xl bg-blox-surface/50 border border-blox-surface animate-slide-up">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Solution Steps</p>
            <ol className="space-y-1">
              {question.workingSteps.map((step, i) => (
                <li key={i} className="text-sm text-slate-300 font-mono">
                  {i + 1}. {step}
                </li>
              ))}
            </ol>
            <p className="text-sm text-blox-xp font-mono mt-2">
              Answer: {question.expectedAnswer}
            </p>
          </div>
        )}
        
        {/* Success celebration */}
        {result === 'correct' && (
          <div className="mt-4 p-4 rounded-xl bg-blox-xp/10 border border-blox-xp/30 animate-pop-in">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <p className="font-display text-blox-xp">Quest Complete!</p>
                <p className="text-sm text-slate-300">
                  +{Math.round(question.xpReward * Math.max(0.5, 1 - ((attempts - 1) * 0.15)))} XP earned
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SCENARIO QUEST HUB - Shows all available quests for a scenario
// ============================================================================

interface ScenarioQuestHubProps {
  scenarioId: string;
  level: MathLevel;
  childId: string;
  masteredLenses?: QuestionLens[];
  onQuestComplete: (lens: QuestionLens, result: { score: number; passed: boolean; xpEarned: number }) => void;
}

export function ScenarioQuestHub({
  scenarioId,
  level,
  childId,
  masteredLenses = [],
  onQuestComplete,
}: ScenarioQuestHubProps) {
  const [activeLens, setActiveLens] = useState<QuestionLens | null>(null);
  
  const scenario = SCENARIOS[scenarioId];
  const availableLenses = getAvailableLenses(level).filter(
    l => scenario?.availableLenses.includes(l)
  );
  
  if (!scenario) return null;
  
  // If we have an active quest, show it
  if (activeLens) {
    return (
      <div>
        <button
          onClick={() => setActiveLens(null)}
          className="mb-4 px-4 py-2 rounded-lg text-sm bg-blox-surface text-slate-300 hover:bg-blox-card transition-colors"
        >
          ← Back to Quest Board
        </button>
        
        <ScenarioQuest
          scenarioId={scenarioId}
          lens={activeLens}
          level={level}
          childId={childId}
          onComplete={(result) => {
            onQuestComplete(activeLens, result);
            setTimeout(() => setActiveLens(null), 2500);
          }}
        />
      </div>
    );
  }
  
  // Show quest selector
  return (
    <div>
      {/* Scenario header */}
      <div className="flex items-center gap-4 mb-6 p-4 rounded-2xl bg-blox-card border border-blox-surface">
        <span className="text-5xl">{scenario.theme.emoji}</span>
        <div className="flex-1">
          <h2 className="font-display text-2xl text-white">{scenario.name}</h2>
          <p className="text-slate-400">{scenario.description}</p>
          <p className="text-sm font-mono text-blox-accent mt-1">{scenario.baseFunction}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Level</p>
          <p className="font-display text-lg text-white">{LEVEL_INFO[level].name}</p>
        </div>
      </div>
      
      {/* Quest grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availableLenses.map(lens => {
          const config = LENS_CONFIGS[lens];
          const isMastered = masteredLenses.includes(lens);
          
          // Calculate potential XP
          const baseXP = 20;
          const xpReward = Math.round(baseXP * LEVEL_INFO[level].xpMultiplier);
          
          return (
            <button
              key={lens}
              onClick={() => !isMastered && setActiveLens(lens)}
              disabled={isMastered}
              className={`text-left p-4 rounded-2xl border transition-all hover:scale-[1.02] ${
                isMastered
                  ? 'bg-blox-xp/10 border-blox-xp/30 opacity-70 cursor-default'
                  : 'bg-blox-card border-blox-surface hover:border-blox-accent cursor-pointer'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg text-white">{config.name}</h3>
                {isMastered ? (
                  <span className="text-blox-xp">✓ Mastered</span>
                ) : (
                  <span className="text-blox-xp font-display">+{xpReward} XP</span>
                )}
              </div>
              
              <p className="text-sm text-slate-400 mb-3">{config.description}</p>
              
              <div className="flex items-center justify-between text-xs">
                <span className="px-2 py-1 rounded bg-blox-surface text-slate-300">
                  {config.conceptualGoal}
                </span>
                {!isMastered && (
                  <span className="text-blox-accent">Start Quest →</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Empty state */}
      {availableLenses.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p className="text-4xl mb-4">🎓</p>
          <p>No quests available at this level for this scenario.</p>
          <p className="text-sm">Try a different level or scenario!</p>
        </div>
      )}
    </div>
  );
}
